import SteppedProgressBar from '@rio-cloud/rio-uikit/SteppedProgressBar';
import { useMemo } from 'react';

import type { CardOrdersResponseType } from '@/api/schema';
import CardTable from '@/features/administration/history/CardTable';
import { OrderStatus } from '@/features/administration/history/OrderBody';
import { useDisplayNames } from '@/hooks/useDisplayNames';
import { t } from '@/lib/utils';

export type OrderBodyDetailsProps = {
    order: CardOrdersResponseType;
    locale: string;
};

const OrderBodyDetails = ({ order, locale }: OrderBodyDetailsProps) => {
    const formatter = useDisplayNames();

    const currentStatus = OrderStatus[order.cardStatus as keyof typeof OrderStatus];

    const timelineLabels = useMemo(
        () => [
            {
                icon: <i className={`rioglyph ${OrderStatus.RECEIVED.icon}`} />,
                label: (
                    <div className='display-flex flex-column text-size-14'>
                        <span className='margin-x-auto max-width-60pct'>{t(OrderStatus.RECEIVED.value)}</span>
                        <span className='text-color-dark'>{new Date(order.orderDate).toLocaleDateString(locale)}</span>
                    </div>
                ),
                labelClassName: 'overflow-visible',
            },
            {
                icon: <i className={`rioglyph ${OrderStatus.PLACED.icon} text-size-20`} />,
                label: <span className='text-size-14'>{t(OrderStatus.PLACED.value)}</span>,
            },
            {
                icon: <i className={`rioglyph ${OrderStatus.SHIPPED.icon} text-size-20`} />,
                label: (
                    <div className='display-flex flex-column text-size-14'>
                        <span className='margin-x-auto max-width-60pct'>{t(OrderStatus.SHIPPED.value)}</span>
                        {order.shippingDate && (
                            <span className='text-color-dark'>
                                {new Date(order.shippingDate).toLocaleDateString(locale)}
                            </span>
                        )}
                    </div>
                ),
                labelClassName: 'overflow-visible',
            },
        ],
        [order.orderDate, order.shippingDate, locale]
    );

    return (
        <>
            <span className='text-color-dark'>{order.user?.email}</span>
            <div className='display-flex justify-content-between margin-top-25'>
                <div className='display-flex flex-basis-60pct'>
                    <div className='display-flex flex-column gap-2 width-80pct'>
                        <span className='text-medium'>{t('orderHistory.orderTimeline')}</span>
                        <SteppedProgressBar
                            variant='circle'
                            selectedStepNumber={currentStatus.id}
                            onSelectedChanged={() => {}}
                            className='margin-top-15'
                            labels={timelineLabels}
                        />
                    </div>
                </div>
                <div className='display-flex flex-basis-20pct'>
                    <div className='display-flex flex-column gap-2'>
                        <span className='text-medium'>{t('general.shippingAddress')}</span>
                        <span>{order.address.firstAddressLine}</span>
                        <span>{order.address.secondAddressLine}</span>
                        <span>{`${order.address.zip} ${order.address.city}`}</span>
                        <span>{formatter?.(order.address.countryCode)}</span>
                    </div>
                </div>
                <div className='display-flex flex-basis-20pct'>
                    {order.address.addressee && (
                        <div className='display-flex flex-column gap-2'>
                            <span className='text-medium'>{t('general.addressee')}</span>
                            <span>{order.address.addressee}</span>
                        </div>
                    )}
                </div>
                <div className='flex-basis-5pct' />
            </div>
            <div className='display-flex flex-column gap-10 margin-top-15'>
                <span className='text-medium'>{`${t('orderHistory.cardsOrdered')} (${order.cards.length})`}</span>
                <CardTable cards={order.cards} />
            </div>
        </>
    );
};

export default OrderBodyDetails;
